import React, { useState, useEffect } from 'react';
import Overlay from "../Overlay";
import Select, { createFilter, components as rsComponents } from "react-select";
import {
  buildRow,
  getLabel,
  getReactSelectAriaLabel,
  getReactSelectAriaLabelMultiselect,
  getLabelForState,
  getLabelForCounty
} from "../../util/FormatUtil";
import SystemAPI from "../../network/SystemAPI";
import Validator, { ValidationEntry } from "../../validation/Validator";
import { Validators } from "../../validation/Validators";
import { sweetalert } from "../../App";
import ExemptionAPI from '../../network/ExemptionAPI';
import AutoComplete from './AutoComplete';
import PhoneInput from 'react-phone-number-input';

// Prevent browser autofill on react-select internal inputs
const NoAutofillInput = (props: any) => (
  <rsComponents.Input {...props} autoComplete="do-not-autofill" />
);
const noAutofillComponents = { Input: NoAutofillInput };

// ─── Types ───────────────────────────────────────────────────────────────────

interface ChildInfo {
  ChildFirstName: string;
  ChildMiddleInitial: string;
  ChildLastName: string;
  MotherMaidenName: string;
  ChildDOB: Date | null;
  ChildBirthCountry: string;
  ChildBirthState: string;
  ChildGenderID: number | null;
  ChildEthnicityID: number | null;
  ChildRaceID: any[];
}

interface ParentInfo {
  ParentFirstName: string;
  ParentMiddleInitial: string;
  ParentLastName: string;
  RelationshipID: number | null;
  ParentAddress: string;
  ParentCity: string;
  ParentState: string;
  ParentZip: string;
  ParentCounty: string;
  ParentAdditionalAddressInfo: string;
  ParentEmail: string;
  ParentPhone: string;
  SearchAddress: string;
  textOptOut: boolean;
}

interface EducationSite {
  schoolOrChildCare: number | null;
  SchoolDistrict: number | null;
  School: number | null;
  ChildCareSite: number | null;
  SchoolGrade: string | null;
  SchoolYear: string | null;
  SchoolPhone: string | null;
  OtherSchool?: string;
  OtherSchoolDistrict?: string;
  OtherChildCareSite?: string;
  isFacilityID?: boolean;
}

interface PhysicianInfo {
  PhysicianFirstName: string;
  PhysicianLastName: string;
  PhysicianAddress: string;
  PhysicianCity: string;
  PhysicianState: string;
  PhysicianZip: string;
  PhysicianAdditionalAddressInfo: string;
  PhysicianPhone: string;
  PhysicianEmail: string;
  SearchAddress: string;
}


// ─── Constants ───────────────────────────────────────────────────────────────

const EMPTY_CHILD_INFO: ChildInfo = {
  ChildFirstName: "",
  ChildMiddleInitial: "",
  ChildLastName: "",
  MotherMaidenName: "",
  ChildDOB: null,
  ChildBirthCountry: "",
  ChildBirthState: "",
  ChildGenderID: null,
  ChildEthnicityID: null,
  ChildRaceID: []
};

const EMPTY_PARENT_INFO: ParentInfo = {
  ParentFirstName: "",
  ParentMiddleInitial: "",
  ParentLastName: "",
  RelationshipID: null,
  ParentAddress: "",
  ParentCity: "",
  // Default to Oklahoma: this is an OSDH form, so the vast majority of records are
  // OK residents. Defaulting the state keeps the County dropdown populated immediately
  // (matching the paper form's County-before-State order) and avoids loading the full
  // national county list. Out-of-state records simply change the State first.
  ParentState: "OK",
  ParentZip: "",
  ParentCounty: "",
  ParentAdditionalAddressInfo: "",
  ParentEmail: "",
  ParentPhone: "",
  SearchAddress: "",
  textOptOut: false
};

const EMPTY_EDUCATION_SITE: EducationSite = {
  schoolOrChildCare: null,
  SchoolDistrict: null,
  School: null,
  ChildCareSite: null,
  SchoolGrade: null,
  SchoolYear: null,
  SchoolPhone: null
};

const EMPTY_PHYSICIAN_INFO: PhysicianInfo = {
  PhysicianFirstName: "",
  PhysicianLastName: "",
  PhysicianAddress: "",
  PhysicianCity: "",
  PhysicianState: "",
  PhysicianZip: "",
  PhysicianAdditionalAddressInfo: "",
  PhysicianPhone: "",
  PhysicianEmail: "",
  SearchAddress: ""
};

const GRADE_OPTIONS = [
  { label: "Headstart", value: "Headstart" },
  { label: "Pre-K", value: "Pre-K" },
  { label: "Kindergarten", value: "Kindergarten" },
  { label: "1st", value: "1st" },
  { label: "2nd", value: "2nd" },
  { label: "3rd", value: "3rd" },
  { label: "4th", value: "4th" },
  { label: "5th", value: "5th" },
  { label: "6th", value: "6th" },
  { label: "7th", value: "7th" },
  { label: "8th", value: "8th" },
  { label: "9th", value: "9th" },
  { label: "10th", value: "10th" },
  { label: "11th", value: "11th" },
  { label: "12th", value: "12th" }
];

const getSchoolYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const options: { label: string; value: string }[] = [];
  for (let i = -1; i <= 1; i++) {
    const startYear = currentYear + i;
    const endYear = startYear + 1;
    options.push({
      label: `${startYear}-${endYear}`,
      value: `${startYear}-${endYear}`
    });
  }
  return options;
};


// ─── Exemption Type Constants ────────────────────────────────────────────────
const EXEMPTION_TYPE_PERSONAL = 1;
const EXEMPTION_TYPE_RELIGIOUS = 2;
const EXEMPTION_TYPE_MEDICAL = 5;

// ─── Component ───────────────────────────────────────────────────────────────

const VaccineExemptionsForm: React.FC = () => {

  // ── Lookup data ──
  const [states, setStates] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [ethnicities, setEthnicities] = useState<any[]>([]);
  const [genders, setGenders] = useState<any[]>([]);
  const [races, setRaces] = useState<any[]>([]);
  const [counties, setCounties] = useState<any[]>([]);
  const [relationships, setRelationships] = useState<any[]>([]);
  const [schools, setSchools] = useState<any[]>([]);
  const [schoolDistricts, setSchoolDistricts] = useState<any[]>([]);
  const [childCareFacilities, setChildCareFacilities] = useState<any[]>([]);
  const [immunizations, setImmunizations] = useState<any[]>([]);
  const [immunizationOptions, setImmunizationOptions] = useState<any[]>([]);
  const [exemptionTypes, setExemptionTypes] = useState<any[]>([]);
  const [exemptionReasons, setExemptionReasons] = useState<any[]>([]);

  // ── Form state ──
  const [childInfo, setChildInfo] = useState<ChildInfo>({ ...EMPTY_CHILD_INFO });
  const [parentInfo, setParentInfo] = useState<ParentInfo>({ ...EMPTY_PARENT_INFO });
  const [educationSites, setEducationSites] = useState<EducationSite[]>([{ ...EMPTY_EDUCATION_SITE }]);
  const [physicianInfo, setPhysicianInfo] = useState<PhysicianInfo>({ ...EMPTY_PHYSICIAN_INFO });

  // ── Exemption-specific state ──
  const [exemptionType, setExemptionType] = useState<number | null>(null);
  const [exemptionReason, setExemptionReason] = useState<number | null>(null);
  const [objectionSummary, setObjectionSummary] = useState("");
  const [exemptImmunizations, setExemptImmunizations] = useState<any[]>([]);
  const [exemptionStatus, setExemptionStatus] = useState<number | null>(1); // null=Pending, 1=Approved (default), 0=Denied
  const [denialReason, setDenialReason] = useState<any>(null);
  const [otherDenialReason, setOtherDenialReason] = useState("");

  // ── Duplicate detection state ──
  const [duplicateCheckPassed, setDuplicateCheckPassed] = useState(false);
  const [duplicateOverridden, setDuplicateOverridden] = useState(false);

  // ── UI state ──
  const [showLoading, setShowLoading] = useState(false);

  const schoolYearOptions = getSchoolYearOptions();


  // ── Load lookup data ──
  useEffect(() => {
    document.title = 'Manual Exemption Submission | Oklahoma Department of Health';

    SystemAPI.getAllStates().then(data => setStates(data));
    SystemAPI.getAllSchoolDistricts().then(data => setSchoolDistricts(data));
    // Source schools/child cares from the Facility table (FacilityType = School/Childcare),
    // matching the patient portal's SchoolInfoModal. The legacy SCHOOLS/CHILDCAREFACILITES
    // tables don't include facilities created via the facility page, so facility-created
    // schools were missing from this dropdown. The backend (ExemptionRoutes.newExemption)
    // already treats EducationSite.School as a Facility ID and stamps isFacilityID.
    SystemAPI.getFacilitySchools().then((data: any) => setSchools(data));
    SystemAPI.getFacilityChildcares().then((data: any) => setChildCareFacilities(data));
    SystemAPI.getAllCountries().then((data: any) => setCountries(data));
    SystemAPI.getAllEthnicities().then(data => setEthnicities(data));
    SystemAPI.getAllGenders().then(data => setGenders(data));
    SystemAPI.getAllRaces().then(data => setRaces(data));
    SystemAPI.getAllImmunizations().then(data => {
      const withAll = [{ label: "All", value: 0 }].concat(data as { label: string; value: number }[]);
      setImmunizations(data as any[]);
      setImmunizationOptions(withAll);
    });
    SystemAPI.getAllExemptionTypes().then((data: any) => setExemptionTypes(data));
    SystemAPI.getAllPersonalExemptionReasons().then((data: any) => setExemptionReasons(data));
    SystemAPI.getAllRelationships().then((data: any) => setRelationships(data));
    SystemAPI.getAllCounties().then(response => {
      let allCountyData = response.counties;
      if (allCountyData && allCountyData.length > 0) {
        allCountyData = allCountyData.map((county) => ({
          label: county.County,
          value: county.State
        }));
      }
      setCounties(allCountyData || []);
    });
  }, []);


  // ── Child info helpers ──
  const updateChild = (field: keyof ChildInfo, value: any) => {
    setChildInfo(prev => ({ ...prev, [field]: value }));
    // Reset duplicate check when name or DOB changes
    if (['ChildFirstName', 'ChildLastName', 'ChildDOB'].includes(field)) {
      setDuplicateCheckPassed(false);
      setDuplicateOverridden(false);
    }
  };

  // ── Parent info helpers ──
  const updateParent = (field: keyof ParentInfo, value: any) => {
    setParentInfo(prev => ({ ...prev, [field]: value }));
  };

  // ── Education site helpers ──
  const updateEducationSite = (index: number, field: string, value: any) => {
    setEducationSites(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Flag facility-sourced selections so downstream lookups (resolveEducationSiteName)
      // know to resolve the ID against the Facility table rather than the legacy list.
      // "Other" (-1) is a free-text entry, not a facility.
      if (field === "School" || field === "ChildCareSite") {
        updated[index].isFacilityID = !!(value && value !== -1);
      }

      // Auto-fill district when school is selected and district is empty
      if (!updated[index].SchoolDistrict && field === "School") {
        const school = schools.find((s) => s.ID === value);
        if (school) {
          updated[index].SchoolDistrict = school.SchoolDistrictID;
        }
      }
      // Reset school when district changes (unless "Other")
      if (field === "SchoolDistrict" && value !== -1 && updated[index].School !== -1) {
        updated[index].School = null;
      }
      return updated;
    });
  };

  const addEducationSite = () => {
    setEducationSites(prev => [...prev, { ...EMPTY_EDUCATION_SITE }]);
  };

  const removeEducationSite = (index: number) => {
    setEducationSites(prev => prev.filter((_, i) => i !== index));
  };

  const resetEducationSite = (index: number, schoolOrChildCareValue: number | null) => {
    setEducationSites(prev => {
      const updated = [...prev];
      updated[index] = { ...EMPTY_EDUCATION_SITE, schoolOrChildCare: schoolOrChildCareValue };
      return updated;
    });
  };

  // ── Physician info helpers ──
  const updatePhysician = (field: keyof PhysicianInfo, value: any) => {
    setPhysicianInfo(prev => ({ ...prev, [field]: value }));
  };


  // ── Reset form ──
  const resetForm = () => {
    setChildInfo({ ...EMPTY_CHILD_INFO });
    setParentInfo({ ...EMPTY_PARENT_INFO });
    setEducationSites([{ ...EMPTY_EDUCATION_SITE }]);
    setPhysicianInfo({ ...EMPTY_PHYSICIAN_INFO });
    setExemptionType(null);
    setExemptionReason(null);
    setObjectionSummary("");
    setExemptImmunizations([]);
    setExemptionStatus(1); // default to Approved
    setDenialReason(null);
    setOtherDenialReason("");
    setDuplicateCheckPassed(false);
    setDuplicateOverridden(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  // ── Duplicate detection ──
  const escapeHtml = (str: string) => {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  };

  const formatDate = (d: any) => {
    if (!d) return '';
    const date = new Date(d);
    if (isNaN(date.getTime())) return '';
    // Use UTC methods to avoid timezone shift (dates stored as UTC midnight)
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${month}/${day}/${year}`;
  };

  const getExemptionTypeLabel = (typeVal: number) => {
    const found = exemptionTypes.find(t => t.value === typeVal);
    return found ? found.label : 'Unknown';
  };

  const getStatusLabel = (status: number | null) => {
    if (status === 1) return 'Approved';
    if (status === 0) return 'Denied';
    return 'Pending';
  };

  const checkForDuplicates = async (): Promise<boolean> => {
    if (!childInfo.ChildFirstName || !childInfo.ChildLastName || !childInfo.ChildDOB || !exemptionType) {
      return true; // Not enough info to check, allow submission
    }

    try {
      const duplicateCheck = await ExemptionAPI.checkDuplicate(
        childInfo.ChildFirstName,
        childInfo.ChildLastName,
        childInfo.ChildDOB instanceof Date ? childInfo.ChildDOB.toISOString() : childInfo.ChildDOB,
        exemptionType
      );

      if (!duplicateCheck.success) {
        return true; // Fail-open
      }

      if (duplicateCheck.status === 'exact_match') {
        const existing = duplicateCheck.existing;
        const result = await sweetalert.fire({
          icon: 'warning',
          title: 'Duplicate Exemption Found',
          html: `<div style="text-align: left;">
            <p>An exemption of this type already exists for this child.</p>
            <table style="width: 100%; margin-bottom: 12px; border-collapse: collapse;">
              <tr><td style="padding: 4px 8px; font-weight: bold; white-space: nowrap; vertical-align: top;">Child</td><td style="padding: 4px 8px;">${escapeHtml(existing.childFirstName)} ${escapeHtml(existing.childLastName)}</td></tr>
              <tr><td style="padding: 4px 8px; font-weight: bold; white-space: nowrap; vertical-align: top;">Date of Birth</td><td style="padding: 4px 8px;">${escapeHtml(formatDate(existing.childDOB))}</td></tr>
              <tr><td style="padding: 4px 8px; font-weight: bold; white-space: nowrap; vertical-align: top;">Exemption Type</td><td style="padding: 4px 8px;">${escapeHtml(getExemptionTypeLabel(existing.exemptionType))}</td></tr>
              <tr><td style="padding: 4px 8px; font-weight: bold; white-space: nowrap; vertical-align: top;">Status</td><td style="padding: 4px 8px;">${escapeHtml(getStatusLabel(existing.status))}</td></tr>
              <tr><td style="padding: 4px 8px; font-weight: bold; white-space: nowrap; vertical-align: top;">Submitted</td><td style="padding: 4px 8px;">${escapeHtml(formatDate(existing.createdDate))}</td></tr>
              <tr><td style="padding: 4px 8px; font-weight: bold; white-space: nowrap; vertical-align: top;">Confirmation #</td><td style="padding: 4px 8px;">${escapeHtml(existing.reqNum)}</td></tr>
            </table>
            <p>If you believe this is a different child or a new request, you may proceed.</p>
          </div>`,
          showCancelButton: true,
          confirmButtonText: 'Proceed Anyway',
          cancelButtonText: 'Cancel',
        });

        setDuplicateCheckPassed(result.isConfirmed);
        setDuplicateOverridden(result.isConfirmed);
        return result.isConfirmed;
      }

      if (duplicateCheck.status === 'possible_match') {
        const candidate = duplicateCheck.candidates[0];
        const result = await sweetalert.fire({
          icon: 'warning',
          title: 'Similar Record Found',
          html: `<div style="text-align: left;">
            <p>We found a similar record that may be for the same child.</p>
            <table style="width: 100%; margin-bottom: 12px; border-collapse: collapse;">
              <tr><td style="padding: 4px 8px;"></td><td style="padding: 4px 8px; font-weight: bold;">Existing Record</td><td style="padding: 4px 8px; font-weight: bold;">Your Entry</td></tr>
              <tr><td style="padding: 4px 8px; font-weight: bold; white-space: nowrap;">Child</td><td style="padding: 4px 8px;">${escapeHtml(candidate.childFirstName)} ${escapeHtml(candidate.childLastName)}</td><td style="padding: 4px 8px;">${escapeHtml(childInfo.ChildFirstName)} ${escapeHtml(childInfo.ChildLastName)}</td></tr>
              <tr><td style="padding: 4px 8px; font-weight: bold; white-space: nowrap;">Date of Birth</td><td style="padding: 4px 8px;">${escapeHtml(formatDate(candidate.childDOB))}</td><td style="padding: 4px 8px;">${escapeHtml(formatDate(childInfo.ChildDOB))}</td></tr>
              <tr><td style="padding: 4px 8px; font-weight: bold; white-space: nowrap;">Confirmation #</td><td style="padding: 4px 8px;">${escapeHtml(candidate.reqNum)}</td><td style="padding: 4px 8px;">—</td></tr>
            </table>
          </div>`,
          showCancelButton: true,
          confirmButtonText: 'This is a different child - continue',
          cancelButtonText: 'Cancel',
        });

        setDuplicateCheckPassed(result.isConfirmed);
        setDuplicateOverridden(result.isConfirmed);
        return result.isConfirmed;
      }

      // status === 'clear'
      setDuplicateCheckPassed(true);
      setDuplicateOverridden(false);
      return true;
    } catch (error) {
      console.error('Error checking for duplicates:', error);
      setDuplicateCheckPassed(true); // Fail-open
      return true;
    }
  };


  // ── Validation ──
  const validateForm = () => {
    // Exemption type
    if (!exemptionType) {
      return { success: false, error: "You must select an Exemption Type" };
    }

    // Child info
    // Race, Ethnicity and Gender are intentionally NOT validated here — they are optional on
    // the manual submission form, since staff entering paper forms often have no demographic
    // data to enter. They are captured when available and sent as null otherwise.
    let childValidator = new Validator<any>()
      .withSimpleValidation("FirstName", Validators.requireNonNullValidator("Child First Name"))
      .withSimpleValidation("LastName", Validators.requireNonNullValidator("Child Last Name"))
      .withValidation("DOB", Validators.requireSchoolAgeDOB("Date of Birth"));

    let childValidation = childValidator.validate({
      FirstName: childInfo.ChildFirstName,
      LastName: childInfo.ChildLastName,
      MotherMaidenName: childInfo.MotherMaidenName,
      DOB: childInfo.ChildDOB,
      BirthCountry: childInfo.ChildBirthCountry,
      BirthState: childInfo.ChildBirthState
    });
    if (!childValidation.success) {
      return { success: false, error: childValidation.error };
    }

    // Parent info
    let parentValidator = new Validator<any>()
      .withSimpleValidation("FirstName", Validators.requireNonNullValidator("Parent First Name"))
      .withSimpleValidation("LastName", Validators.requireNonNullValidator("Parent Last Name"))
      .withSimpleValidation("Relationship", Validators.requireNonNullValidator("Relationship"))
      .withSimpleValidation("Address", Validators.requireNonNullValidator())
      .withSimpleValidation("City", Validators.requireNonNullValidator())
      .withSimpleValidation("State", Validators.requireNonNullValidator())
      .withComposedValidation("Zipcode", new ValidationEntry(Validators.requireNonNullValidator()), new ValidationEntry(Validators.requireZip("ZIP Code")))
      .withComposedValidation("Phone", new ValidationEntry(Validators.requireNonNullValidator("Phone")), new ValidationEntry(Validators.requirePhone("Phone")));

    // Only validate email format if one was provided
    if (parentInfo.ParentEmail && parentInfo.ParentEmail.trim()) {
      parentValidator = parentValidator.withComposedValidation("Email", new ValidationEntry(Validators.requireValidEmail("Email")), Validators.requireNotBlankAndLength(100, "Email"));
    }

    let parentValidation = parentValidator.validate({
      FirstName: parentInfo.ParentFirstName,
      LastName: parentInfo.ParentLastName,
      Relationship: parentInfo.RelationshipID,
      Address: parentInfo.ParentAddress,
      City: parentInfo.ParentCity,
      State: parentInfo.ParentState,
      Zipcode: parentInfo.ParentZip,
      County: parentInfo.ParentCounty,
      Phone: parentInfo.ParentPhone,
      ...(parentInfo.ParentEmail && parentInfo.ParentEmail.trim() ? { Email: parentInfo.ParentEmail } : {})
    });
    if (!parentValidation.success) {
      return { success: false, error: parentValidation.error };
    }

    // School info — not required for HL7, entire section is optional

    // Exemption-specific validation
    const exemptImmunizationsCheck = exemptImmunizations.length ? exemptImmunizations : null;

    if (exemptionType === EXEMPTION_TYPE_MEDICAL) {
      // Medical
      if (!exemptImmunizationsCheck) {
        return { success: false, error: "Exempt immunizations cannot be empty" };
      }
      let physValidator = new Validator<any>()
        .withSimpleValidation("FirstName", Validators.requireNonNullValidator("Physician First Name"))
        .withSimpleValidation("LastName", Validators.requireNonNullValidator("Physician Last Name"))
        .withSimpleValidation("Address", Validators.requireNonNullValidator("Physician Address"));

      let physValidation = physValidator.validate({
        FirstName: physicianInfo.PhysicianFirstName,
        LastName: physicianInfo.PhysicianLastName,
        Address: physicianInfo.PhysicianAddress,
      });
      if (!physValidation.success) {
        return { success: false, error: physValidation.error };
      }
      // Validate physician zip format if provided (optional field)
      if (physicianInfo.PhysicianZip && !/^\d{5}(-\d{4})?(?!-)$/.test(physicianInfo.PhysicianZip)) {
        return { success: false, error: "Physician Zipcode is not a valid zipcode." };
      }
    } else if (exemptionType === EXEMPTION_TYPE_RELIGIOUS) {
      // Religious
      if (!exemptImmunizationsCheck) {
        return { success: false, error: "Exempt immunizations cannot be empty" };
      }
    } else if (exemptionType === EXEMPTION_TYPE_PERSONAL) {
      // Personal
      if (!exemptImmunizationsCheck) {
        return { success: false, error: "Exempt immunizations cannot be empty" };
      }
      if (exemptionReason === 7 && !objectionSummary) {
        return { success: false, error: "Objection Summary cannot be empty" };
      }
    }

    return { success: true };
  };


  // ── Submit ──
  const submitForm = async () => {
    setShowLoading(true);
    const validation = validateForm();
    if (!validation.success) {
      setShowLoading(false);
      return sweetalert.fire({ icon: "error", title: "", text: validation.error });
    }

    // Run duplicate check if not already passed/overridden
    if (!duplicateCheckPassed && !duplicateOverridden) {
      setShowLoading(false);
      const canProceed = await checkForDuplicates();
      if (!canProceed) return;
      setShowLoading(true);
    }

    // Build race array: react-select hands back option objects, so map them down to IDs.
    // Race is optional, and the empty case is normalized to null where the payload is
    // assembled below — not on this line.
    let raceArray = childInfo.ChildRaceID || [];
    if (raceArray.length > 0 && typeof raceArray[0] === 'object') {
      raceArray = raceArray.map(obj => obj?.value);
    }

    // Build immunization arrays
    const exemptImmunizationsArray = exemptImmunizations.map(obj => obj.value);

    // Clean parent info (remove SearchAddress)
    const cleanParentInfo = { ...parentInfo };
    delete (cleanParentInfo as any).SearchAddress;

    // Clean physician info (remove SearchAddress)
    const cleanPhysicianInfo = { ...physicianInfo };
    delete (cleanPhysicianInfo as any).SearchAddress;

    const exemptionInfoCopy = {
      ExemptionType: exemptionType,
      ExemptionReason: exemptionReason || null,
      ObjectionSummary: objectionSummary || null,
      ExemptImmunizations: exemptImmunizationsArray,
      ContraImmunizations: [],
      acknowledged: true,
      ManualStatus: exemptionStatus,
      DenialReason: exemptionStatus === 0 && denialReason ? denialReason.value : null,
      OtherDenialReason: exemptionStatus === 0 && denialReason?.value === "Other" ? otherDenialReason : null,
      childInfo: {
        ...childInfo,
        ChildFirstName: childInfo.ChildFirstName?.trim() || null,
        ChildMiddleInitial: childInfo.ChildMiddleInitial?.trim() || null,
        ChildLastName: childInfo.ChildLastName?.trim() || null,
        ChildBirthCountry: childInfo.ChildBirthCountry?.trim() || null,
        ChildBirthState: childInfo.ChildBirthState?.trim() || null,
        ChildRaceID: raceArray.length ? raceArray : null
      },
      parentInfo: {
        ...cleanParentInfo,
        ParentFirstName: cleanParentInfo.ParentFirstName?.trim() || null,
        ParentMiddleInitial: cleanParentInfo.ParentMiddleInitial?.trim() || null,
        ParentLastName: cleanParentInfo.ParentLastName?.trim() || null,
        ParentAddress: cleanParentInfo.ParentAddress?.trim() || null,
        ParentAdditionalAddressInfo: cleanParentInfo.ParentAdditionalAddressInfo?.trim() || null,
        ParentCity: cleanParentInfo.ParentCity?.trim() || null,
        ParentZip: cleanParentInfo.ParentZip?.trim() || null,
        ParentCounty: cleanParentInfo.ParentCounty?.trim() || null,
        ParentState: cleanParentInfo.ParentState?.trim() || null,
        ParentEmail: cleanParentInfo.ParentEmail?.trim() || null,
        ParentPhone: cleanParentInfo.ParentPhone?.trim() || null,
      },
      schoolInfo: educationSites.map(site => ({
        ...site,
        SchoolGrade: site.SchoolGrade?.trim() || null,
        SchoolPhone: site.SchoolPhone?.trim() || null,
      })),
      physicianInfo: exemptionType === EXEMPTION_TYPE_MEDICAL ? {
        ...cleanPhysicianInfo,
        PhysicianFirstName: cleanPhysicianInfo.PhysicianFirstName?.trim() || null,
        PhysicianLastName: cleanPhysicianInfo.PhysicianLastName?.trim() || null,
        PhysicianAddress: cleanPhysicianInfo.PhysicianAddress?.trim() || null,
        PhysicianAdditionalAddressInfo: cleanPhysicianInfo.PhysicianAdditionalAddressInfo?.trim() || null,
        PhysicianCity: cleanPhysicianInfo.PhysicianCity?.trim() || null,
        PhysicianZip: cleanPhysicianInfo.PhysicianZip?.trim() || null,
        PhysicianState: cleanPhysicianInfo.PhysicianState?.trim() || null,
        PhysicianEmail: cleanPhysicianInfo.PhysicianEmail?.trim() || null,
        PhysicianPhone: cleanPhysicianInfo.PhysicianPhone?.trim() || null,
      } : null
    };

    // ── Confirmation modal ──
    const exemptionTypeLabel = exemptionTypes.find(t => t.value === exemptionType)?.label || 'Unknown';
    const confirm = await sweetalert.fire({
      icon: "question",
      title: "Confirm Submission",
      html: `<div style="text-align: left;">
        <p>Please confirm the following exemption submission:</p>
        <table style="width: 100%; border-collapse: collapse;">
          <tr><td style="padding: 4px 8px; font-weight: bold;">Child</td><td style="padding: 4px 8px;">${escapeHtml(childInfo.ChildFirstName)} ${escapeHtml(childInfo.ChildLastName)}</td></tr>
          <tr><td style="padding: 4px 8px; font-weight: bold;">Date of Birth</td><td style="padding: 4px 8px;">${escapeHtml(formatDate(childInfo.ChildDOB))}</td></tr>
          <tr><td style="padding: 4px 8px; font-weight: bold;">Exemption Type</td><td style="padding: 4px 8px;">${escapeHtml(exemptionTypeLabel)}</td></tr>
          <tr><td style="padding: 4px 8px; font-weight: bold;">Parent/Guardian</td><td style="padding: 4px 8px;">${escapeHtml(parentInfo.ParentFirstName)} ${escapeHtml(parentInfo.ParentLastName)}</td></tr>
          <tr><td style="padding: 4px 8px; font-weight: bold;">Status</td><td style="padding: 4px 8px;">${exemptionStatus === 1 ? 'Approved' : exemptionStatus === 0 ? 'Denied' : 'Pending'}</td></tr>
        </table>
      </div>`,
      showCancelButton: true,
      confirmButtonText: "Submit",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) {
      setShowLoading(false);
      return;
    }

    try {
      const res = await ExemptionAPI.newExemption(exemptionInfoCopy, null);
      setShowLoading(false);
      if (res.success) {
        sweetalert.fire({
          icon: "success",
          title: "Exemption Submitted",
          text: "The exemption has been submitted successfully. The form has been cleared for the next entry.",
        });
        resetForm();
      } else {
        //@ts-ignore
        if (res?.message && res.message.includes("File")) {
          sweetalert.fire({ icon: "error", title: "File size exceeds the 2MB limit. Please upload a smaller file.", text: "Please try again!" });
        } else {
          sweetalert.fire({ icon: "error", title: "Server Error", text: "Please try again!" });
        }
      }
    } catch (err) {
      setShowLoading(false);
      sweetalert.fire({ icon: "error", title: "Server Error", text: "Please try again!" });
    }
  };


  // ── Physician approval form download ──


  // ── Prepare dropdown options ──
  let countriesCopy = countries && countries.length > 0 ? JSON.parse(JSON.stringify(countries)) : [];
  if (countriesCopy.length > 0) {
    const indexOfUS = countriesCopy.findIndex(obj => obj.value === 'United States');
    if (indexOfUS > -1) {
      const [removedObj] = countriesCopy.splice(indexOfUS, 1);
      countriesCopy.unshift(removedObj);
    }
  }

  const translationGenders = (genders || []).map(e => ({ label: e.label, value: e.value }));
  const translationEthnicities = (ethnicities || []).map(e => ({ label: e.label, value: e.value }));
  const translationRaces = (races || []).map(e => ({ label: e.label, value: e.value }));
  const translationRelationships = (relationships || []).map(e => ({ label: e.label, value: e.value }));
  const translationExemptionTypes = (exemptionTypes || []).map(e => ({ label: e.label, value: e.value }));
  const translationPersonalExemptionReasons = (exemptionReasons || []).map(e => ({ label: e.label, value: e.value }));

  // County options filtered by selected state
  const getCountyOptions = (stateValue: string) => {
    if (!counties || counties.length === 0) return [];
    if (stateValue) {
      const stateName = (states || []).find(s => s.value === stateValue)?.label;
      return counties
        .filter(c => c.value === stateName || (c as any).originalValue === stateName)
        .map(c => ({ label: c.label, value: c.label, originalValue: c.value }));
    }
    return counties.map(c => ({ label: c.label, value: c.label, originalValue: c.value }));
  };


  // ─── RENDER ────────────────────────────────────────────────────────────────

  return (
    <div className="container-fluid min-vh-100">
      <Overlay show_loading={showLoading} />
      <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
      <div className="row">
        <div className="col-12 col-sm-12 col-md-12 col-lg-8 offset-lg-2 col-xl-6 offset-xl-3 pt-2">
          <main id="main-content" tabIndex={-1} aria-label="Manual Exemption Submission">


            {/* ═══════════════════════════════════════════════════════════════
                EXEMPTION INFORMATION (matches PDF field order)
                ═══════════════════════════════════════════════════════════════ */}
            <div className="card mt-2">
              <div className="card-header verlag-bold">
                <h4 className="m-0">Exemption Information</h4>
              </div>
              <div className="card-body">

                {/* ── Row 1: Child Name (Last, First, MI) ── */}
                {buildRow('Child Last Name',
                  <input id="ChildLastName" className="form-control" maxLength={50} autoComplete="do-not-autofill" type="search"
                    name="ChildLastName" aria-label="Last Name Of Child"
                    onChange={(e) => updateChild('ChildLastName', e.target.value)}
                    value={childInfo.ChildLastName}
                  />, 'Last Name Of Child', true
                )}
                {buildRow('Child First Name',
                  <input id="ChildFirstName" className="form-control" maxLength={50} autoComplete="do-not-autofill" type="search"
                    name="ChildFirstName" aria-label="First Name Of Child"
                    onChange={(e) => updateChild('ChildFirstName', e.target.value)}
                    value={childInfo.ChildFirstName}
                  />, 'First Name Of Child', true
                )}
                {buildRow('Child Middle Initial',
                  <input id="ChildMiddleInitial" className="form-control" maxLength={5} autoComplete="do-not-autofill" type="search"
                    name="ChildMiddleInitial" aria-label="Middle Initial Of Child"
                    onChange={(e) => updateChild('ChildMiddleInitial', e.target.value)}
                    value={childInfo.ChildMiddleInitial}
                  />, 'Middle Initial Of Child'
                )}

                {/* ── Row 2: Birth Date, Birth Country, Birth State ── */}
                {buildRow("Birth Date",
                  <input id="ChildDateOfBirth" min="1000-01-01" max={new Date().toISOString().split('T')[0]}
                    className="form-control" type="date" name="DOB" aria-label="Child Date of Birth"
                    value={childInfo.ChildDOB ? new Date(childInfo.ChildDOB).toISOString().split('T')[0] : ''}
                    onChange={(e) => {
                      if (Date.parse(e.target.value)) {
                        updateChild('ChildDOB', new Date(e.target.value));
                      }
                    }}
                  />, "Child Date of Birth", true
                )}
                {buildRow('Birth Country',
                  <Select components={noAutofillComponents} key="ChildBirthCountry" id="ChildBirthCountry" isSearchable={true}
                    placeholder={<div className="accessibilityText">Please Select...</div>}
                    noOptionsMessage={() => "No option"}
                    aria-label={getReactSelectAriaLabel("Birth Country", getLabel(childInfo.ChildBirthCountry, countries))}
                    value={getLabel(childInfo.ChildBirthCountry, countries)}
                    onChange={(e: any) => updateChild('ChildBirthCountry', e.value)}
                    className="state_select"
                    options={countriesCopy}
                  />, 'Birth Country'
                )}
                {buildRow('Birth State',
                  <Select components={noAutofillComponents} key="ChildBirthState" id="ChildBirthState" isSearchable={true}
                    placeholder={<div className="accessibilityText">Please Select...</div>}
                    noOptionsMessage={() => "No option"}
                    aria-label="Birth State"
                    value={getLabel(childInfo.ChildBirthState, states)}
                    onChange={(e: any) => updateChild('ChildBirthState', e.value)}
                    className="state_select"
                    options={states}
                  />, 'Birth State'
                )}

                <hr />

                {/* ── Row 3: Parent Name, Mother's Maiden Name, Parent Address ── */}
                {buildRow("Parent/Guardian First Name",
                  <input id="ParentName" className="form-control" maxLength={50} autoComplete="do-not-autofill" type="search"
                    name="ParentFirstName" aria-label="Parent or Guardian Name"
                    onChange={(e) => updateParent('ParentFirstName', e.target.value)}
                    value={parentInfo.ParentFirstName}
                  />, "Parent or Guardian's First Name", true
                )}
                {buildRow("Parent/Guardian Last Name",
                  <input id="ParentLastName" className="form-control" maxLength={50} autoComplete="do-not-autofill" type="search"
                    name="ParentLastName" aria-label="Last Name Of Parent"
                    onChange={(e) => updateParent('ParentLastName', e.target.value)}
                    value={parentInfo.ParentLastName}
                  />, 'Last Name Of Parent', true
                )}
                {buildRow("Mother's Maiden Name",
                  <input id="MothersMaidenName" className="form-control" maxLength={50} autoComplete="do-not-autofill" type="search"
                    name="MothersMaidenName" aria-label="Mother's Maiden Name"
                    onChange={(e) => updateChild('MotherMaidenName', e.target.value)}
                    value={childInfo.MotherMaidenName}
                  />, "Mother's Maiden Name"
                )}

                {/* ── Row 4: Parent Address ── */}
                <div className="form-group row" data-toggle="tooltip" data-placement="top" title="Address">
                  <label id="Address" htmlFor="Parentautocomplete" className="col-12 col-sm-4 col-form-label px-3">Search Address</label>
                  <div className="col-12 col-sm-8 p-0 m-0 text-center text-md-left" id="Address">
                    <AutoComplete key="Parentautocomplete" id="Parentautocomplete"
                      onChange={(addr) => updateParent('SearchAddress', addr)}
                      value={parentInfo.SearchAddress}
                      updateFormData={(addr, city, state, zip, county, country, subpremise) => {
                        setParentInfo(prev => ({
                          ...prev,
                          ParentAddress: addr,
                          ParentCity: city,
                          ParentState: state,
                          ParentZip: zip,
                          ParentCounty: county,
                          ...(subpremise ? { ParentAdditionalAddressInfo: subpremise } : {})
                        }));
                      }}
                    />
                  </div>
                </div>
                {buildRow("Parent/Guardian Street Address",
                  <input id="ParentStreetAddress" className="form-control" maxLength={50} autoComplete="do-not-autofill" type="search"
                    name="parent_addr_nofill" aria-label="Street Address"
                    onChange={(e) => updateParent('ParentAddress', e.target.value)}
                    value={parentInfo.ParentAddress}
                  />, 'Street Address', true
                )}
                {buildRow('Apartment/Suite/Building #',
                  <input id="ParentAdditionalAddress" className="form-control" maxLength={50} autoComplete="do-not-autofill" type="search"
                    name="parent_apt_nofill" aria-label="Apartment/Suite/Building #"
                    onChange={(e) => updateParent('ParentAdditionalAddressInfo', e.target.value)}
                    value={parentInfo.ParentAdditionalAddressInfo}
                  />, 'Apartment/Suite/Building #'
                )}

                {/* ── Row 5: County, City, State, ZIP (PDF order) ── */}
                {buildRow('County',
                  <Select
                    components={noAutofillComponents}
                    key={`county-select-${parentInfo.ParentState || 'no-state'}`}
                    id="ParentCounty" isSearchable={true}
                    // Counties are filtered by state, so the field is only meaningful once a
                    // state is selected. Disabling it (rather than rendering the full national
                    // county list) keeps the option list small and avoids the render hang.
                    isDisabled={!parentInfo.ParentState}
                    placeholder={<div className="accessibilityText">{parentInfo.ParentState ? "Please Select..." : "Select a state first"}</div>}
                    noOptionsMessage={() => "No option"}
                    aria-label="County"
                    value={(() => { const v = getLabelForCounty(parentInfo.ParentCounty, counties, parentInfo.ParentState, states); return v ? { ...v, originalValue: v.value } : null; })()}
                    options={getCountyOptions(parentInfo.ParentState)}
                    filterOption={(option, input) => {
                      const label = option.label?.toString().toLowerCase() || '';
                      return label.includes(input.toLowerCase());
                    }}
                    onChange={(e: any) => updateParent('ParentCounty', e ? e.label : '')}
                    className="state_select"
                  />, 'County'
                )}
                {buildRow('City',
                  <input id="ParentCity" className="form-control" maxLength={50} autoComplete="do-not-autofill" type="search"
                    name="parent_city_nofill" aria-label="City"
                    onChange={(e) => updateParent('ParentCity', e.target.value)}
                    value={parentInfo.ParentCity}
                  />, 'City', true
                )}
                {buildRow('State',
                  <Select components={noAutofillComponents} key="ParentState" id="ParentState" isSearchable={true}
                    placeholder={<div className="accessibilityText">Please Select...</div>}
                    noOptionsMessage={() => "No option"}
                    aria-label="State"
                    value={parentInfo.ParentState ? getLabelForState(parentInfo.ParentState, states) : null}
                    // County is filtered by state, so changing the state must clear any
                    // previously-selected county. Otherwise a stale county stays in state,
                    // disappears from the (now state-filtered) display, and is silently submitted.
                    onChange={(e: any) => setParentInfo(prev => ({ ...prev, ParentState: e.value, ParentCounty: "" }))}
                    className="state_select"
                    options={states}
                  />, 'State', true
                )}
                {buildRow('ZIP Code',
                  <input id="ParentZipcode" className="form-control" maxLength={50} autoComplete="do-not-autofill" type="search"
                    name="parent_zip_nofill" aria-label="ZIP Code"
                    onChange={(e) => updateParent('ParentZip', e.target.value)}
                    value={parentInfo.ParentZip}
                  />, 'ZIP Code', true
                )}

                {/* ── Row 6: Phone, Email ── */}
                {buildRow('Parent/Guardian Phone Number',
                  <PhoneInput id="ParentPhone" placeholder="Enter phone number (10 digit)"
                    onChange={(e) => updateParent('ParentPhone', e)}
                    defaultCountry="US" aria-label="Parent/Guardian Phone Number"
                    value={parentInfo.ParentPhone}
                  />, 'Parent/Guardian Phone Number', true
                )}
                {buildRow('Parent/Guardian Email',
                  <input id="ParentEmail" className="form-control" maxLength={100} autoComplete="do-not-autofill" type="search"
                    name="ParentEmail" placeholder="name@example.com" aria-label="Parent/Guardian Email"
                    onChange={(e) => updateParent('ParentEmail', e.target.value)}
                    value={parentInfo.ParentEmail}
                  />, 'Parent/Guardian Email'
                )}
                {buildRow("Relationship to Child",
                  <Select components={noAutofillComponents} key="RelationshipID" id="RelationshipID" isSearchable={true}
                    placeholder={<div className="accessibilityText">Please Select...</div>}
                    noOptionsMessage={() => "No option"}
                    aria-label="Relationship to Child"
                    value={getLabel(parentInfo.RelationshipID, relationships)}
                    onChange={(e: any) => updateParent('RelationshipID', e.value)}
                    className="state_select"
                    options={translationRelationships}
                  />, 'Relationship to Child', true
                )}

                <hr />

                {/* ── Row 7: School / Child Care ── */}
                {educationSites.map((site, index) => (
                  <section key={`site${index}`}>
                    {buildRow('School / Child Care',
                      <Select components={noAutofillComponents} key="School/Childcare" id="School/Childcare" isClearable={true} isSearchable={true}
                        placeholder="Please Select..."
                        noOptionsMessage={() => "No option"}
                        aria-label={getReactSelectAriaLabel("School/ Child Care", getLabel(site.schoolOrChildCare, [{ label: "School", value: 0 }, { label: "Child Care", value: 1 }]))}
                        value={getLabel(site.schoolOrChildCare, [{ label: "School", value: 0 }, { label: "Child Care", value: 1 }])}
                        onChange={(e: any) => resetEducationSite(index, e?.value ?? null)}
                        className="state_select"
                        options={[{ label: "School", value: 0 }, { label: "Child Care", value: 1 }]}
                      />, 'School/ Child Care'
                    )}

                    {/* School path */}
                    {site.schoolOrChildCare === 0 && (<>
                      {buildRow('School District',
                        <Select components={noAutofillComponents} key="SchoolDistrict" id="SchoolDistrict" isClearable={true} isSearchable={true}
                          filterOption={createFilter({ ignoreAccents: false })}
                          placeholder="Please Select..."
                          noOptionsMessage={() => "No option"}
                          aria-label={getReactSelectAriaLabel("School District", site.SchoolDistrict ? getLabel(site.SchoolDistrict, schoolDistricts) : null)}
                          value={site.SchoolDistrict ? getLabel(site.SchoolDistrict, schoolDistricts) : null}
                          onChange={(e: any) => updateEducationSite(index, "SchoolDistrict", e?.value)}
                          className="state_select"
                          options={schoolDistricts}
                        />, 'School District'
                      )}
                      {site.SchoolDistrict === -1 && buildRow('Other School District',
                        <input id="OtherSchoolDistrict" className="form-control" maxLength={50} autoComplete="do-not-autofill" type="search"
                          name="SchoolDistrict" aria-label="School District"
                          onChange={(e) => updateEducationSite(index, "OtherSchoolDistrict", e.target.value)}
                          value={site.OtherSchoolDistrict || ''}
                        />, 'School District'
                      )}
                      {buildRow('Name of School',
                        <Select components={noAutofillComponents} key="SchoolName" id="SchoolName" isClearable={true} isSearchable={true}
                          filterOption={createFilter({ ignoreAccents: false })}
                          placeholder="Please Select..."
                          noOptionsMessage={() => "No option"}
                          aria-label={getReactSelectAriaLabel("Name of School", site.School ? getLabel(site.School, schools.map(s => ({ label: s.FacilityName, value: s.ID }))) : null)}
                          value={site.School ? getLabel(site.School, schools.map(s => ({ label: s.FacilityName, value: s.ID }))) : null}
                          onChange={(e: any) => updateEducationSite(index, "School", e?.value)}
                          className="state_select"
                          options={site.SchoolDistrict && site.SchoolDistrict !== -1
                            ? schools.filter(s => s.SchoolDistrictID === site.SchoolDistrict).map(s => ({ label: s.FacilityName, value: s.ID }))
                            : schools.map(s => ({ label: s.FacilityName, value: s.ID }))}
                        />, 'Name Of School'
                      )}
                      {site.School === -1 && buildRow('Other Name of School',
                        <input id="OtherSchoolName" className="form-control" maxLength={50} autoComplete="do-not-autofill" type="search"
                          name="SchoolName" aria-label="Name of School"
                          onChange={(e) => updateEducationSite(index, "OtherSchool", e.target.value)}
                          value={site.OtherSchool || ''}
                        />, 'Name of School'
                      )}
                    </>)}

                    {/* Child Care path */}
                    {site.schoolOrChildCare === 1 && (<>
                      {buildRow('Name of Child Care',
                        <Select components={noAutofillComponents} key="ChildCareSite" id="ChildCareSite" isSearchable={true} isClearable={true}
                          filterOption={createFilter({ ignoreAccents: false })}
                          placeholder="Please Select..."
                          noOptionsMessage={() => "No option"}
                          aria-label={getReactSelectAriaLabel("Name of Child Care", site.ChildCareSite ? getLabel(site.ChildCareSite, childCareFacilities.map(f => ({ label: f.FacilityName, value: f.ID }))) : null)}
                          value={site.ChildCareSite ? getLabel(site.ChildCareSite, childCareFacilities.map(f => ({ label: f.FacilityName, value: f.ID }))) : null}
                          onChange={(e: any) => updateEducationSite(index, "ChildCareSite", e?.value)}
                          className="state_select"
                          options={childCareFacilities.map(f => ({ label: f.FacilityName, value: f.ID }))}
                        />, 'Name of Child Care'
                      )}
                      {site.ChildCareSite === -1 && buildRow('Other Name of Child Care',
                        <input id="OtherChildCareSite" className="form-control" maxLength={50} autoComplete="do-not-autofill" type="search"
                          name="ChildCareSite" aria-label="Child Care Site"
                          onChange={(e) => updateEducationSite(index, "OtherChildCareSite", e.target.value)}
                          value={site.OtherChildCareSite || ''}
                        />, 'Other Child Care Site'
                      )}
                    </>)}

                    {/* School Year, Grade (PDF order: District, Year, Grade on same line) */}
                    {(site.schoolOrChildCare === 0 || site.schoolOrChildCare === 1) && buildRow('School Year',
                      <Select components={noAutofillComponents} key="SchoolYear" id="SchoolYear" isClearable={true} isSearchable={true}
                        placeholder="Please Select..."
                        noOptionsMessage={() => "No option"}
                        aria-label={getReactSelectAriaLabel("School Year", site.SchoolYear ? getLabel(site.SchoolYear, schoolYearOptions) : null)}
                        value={site.SchoolYear ? getLabel(site.SchoolYear, schoolYearOptions) : null}
                        onChange={(e: any) => updateEducationSite(index, "SchoolYear", e?.value)}
                        className="state_select"
                        options={schoolYearOptions}
                      />, 'School Year'
                    )}
                    {site.schoolOrChildCare === 0 && buildRow('Grade',
                      <Select components={noAutofillComponents} key="SchoolGrade" id="SchoolGrade" isClearable={true} isSearchable={true}
                        placeholder="Please Select..."
                        noOptionsMessage={() => "No option"}
                        aria-label={getReactSelectAriaLabel("Grade", site.SchoolGrade ? getLabel(site.SchoolGrade, GRADE_OPTIONS) : null)}
                        value={site.SchoolGrade ? getLabel(site.SchoolGrade, GRADE_OPTIONS) : null}
                        onChange={(e: any) => updateEducationSite(index, "SchoolGrade", e?.value)}
                        className="state_select"
                        options={GRADE_OPTIONS}
                      />, 'Grade'
                    )}

                    {/* Facility Phone */}
                    {site.schoolOrChildCare === 0 && buildRow('Facility Phone Number',
                      <PhoneInput id="SchoolPhone" placeholder="Enter phone number (10 digit)"
                        onChange={(e) => updateEducationSite(index, "SchoolPhone", e)}
                        defaultCountry="US" aria-label="Facility Phone Number"
                        value={site.SchoolPhone || ''}
                      />, 'Facility Phone Number'
                    )}
                    {site.schoolOrChildCare === 1 && buildRow('Facility Phone Number',
                      <PhoneInput id="ChildcarePhone" placeholder="Enter phone number (10 digit)"
                        onChange={(e) => updateEducationSite(index, "SchoolPhone", e)}
                        defaultCountry="US" aria-label="Facility Phone Number"
                        value={site.SchoolPhone || ''}
                      />, 'Facility Phone Number'
                    )}
                  </section>
                ))}

                <hr />

                {/* ── Row 8: Race, Ethnicity, Gender (bottom of shared section on PDF) ── */}
                {buildRow('Race (Limit 3)',
                  <Select components={noAutofillComponents} key="ChildRaceID" id="ChildRaceID" isSearchable={true} isMulti={true}
                    placeholder={<div className="accessibilityText">Please Select...</div>}
                    isOptionDisabled={() => childInfo.ChildRaceID?.length === 3}
                    noOptionsMessage={() => "No option"}
                    aria-label="Child Race"
                    value={childInfo.ChildRaceID}
                    onChange={(e: any) => updateChild('ChildRaceID', e ? e : [])}
                    className="state_select"
                    options={translationRaces}
                  />, 'Child Race'
                )}
                {buildRow('Ethnicity',
                  <Select components={noAutofillComponents} key="ChildEthnicityID" id="ChildEthnicityID" isSearchable={true}
                    placeholder={<div className="accessibilityText">Please Select...</div>}
                    noOptionsMessage={() => "No option"}
                    aria-label="Child Ethnicity"
                    isClearable={true}
                    value={getLabel(childInfo.ChildEthnicityID, ethnicities)}
                    onChange={(e: any) => updateChild('ChildEthnicityID', e ? e.value : null)}
                    className="state_select"
                    options={translationEthnicities}
                  />, 'Child Ethnicity'
                )}
                {buildRow("Child's Gender",
                  <Select components={noAutofillComponents} key="ChildGenderID" id="ChildGenderID" isSearchable={true}
                    placeholder={<div className="accessibilityText">Please Select...</div>}
                    noOptionsMessage={() => "No option"}
                    aria-label="Child Gender"
                    isClearable={true}
                    value={getLabel(childInfo.ChildGenderID, genders)}
                    onChange={(e: any) => updateChild('ChildGenderID', e ? e.value : null)}
                    className="state_select"
                    options={translationGenders}
                  />, 'Child Gender'
                )}
              </div>
            </div>




            {/* ═══════════════════════════════════════════════════════════════
                SECTION 4: TYPE OF EXEMPTION
                ═══════════════════════════════════════════════════════════════ */}
            <div className="card mt-2">
              <div className="card-header verlag-bold">
                <h4 className="m-0">Type of Exemption</h4>
              </div>
              <div className="card-body">
                {buildRow('Type Of Exemption',
                  <Select components={noAutofillComponents} key="TypeOfExemption" id="TypeOfExemption" isSearchable={true}
                    menuPlacement="auto"
                    aria-label={getReactSelectAriaLabel("Type of Exemption", exemptionType ? getLabel(exemptionType, exemptionTypes) : null)}
                    placeholder={<div className="accessibilityText">Please Select...</div>}
                    noOptionsMessage={() => "No option"}
                    value={exemptionType ? getLabel(exemptionType, exemptionTypes) : null}
                    onChange={(e: any) => {
                      setExemptionType(e.value);
                      setExemptImmunizations([]);
                      setObjectionSummary("");
                      setExemptionReason(null);
                      setDuplicateCheckPassed(false);
                      setDuplicateOverridden(false);
                    }}
                    className="state_select w-100"
                    options={translationExemptionTypes}
                  />, 'Type Of Exemption', true
                )}


                {/* ── Religious Exemption (Type 2) ── */}
                {exemptionType === EXEMPTION_TYPE_RELIGIOUS && (
                  <div className="alert alert-info mt-2">
                    <p className="mb-0 text-center font-italic">
                      I hereby certify that immunization is contrary to the teachings of the above-named child's religion.
                    </p>
                  </div>
                )}

                {/* ── Personal Exemption (Type 1) ── */}
                {exemptionType === EXEMPTION_TYPE_PERSONAL && (<>
                  {buildRow('Reason',
                    <Select components={noAutofillComponents} key="ExemptionReason" id="ExemptionReason" isSearchable={true}
                      menuPlacement="auto"
                      aria-label={getReactSelectAriaLabel("Reason", getLabel(exemptionReason, exemptionReasons))}
                      placeholder={<div className="accessibilityText">Please Select...</div>}
                      noOptionsMessage={() => "No option"}
                      value={exemptionReason ? getLabel(exemptionReason, exemptionReasons) : null}
                      onChange={(e: any) => setExemptionReason(e.value)}
                      className="state_select"
                      options={translationPersonalExemptionReasons}
                    />, 'Exemption Reason'
                  )}
                  {exemptionReason === 7 && (
                    <div className="form-group row" data-toggle="tooltip" data-placement="top" title="Summary of Objection">
                      <label id="SummaryOfObjection" htmlFor="SummaryOfObjection" className="col-12 col-sm-4 col-form-label text-left px-2">Summary of Objection</label>
                      <div className="col-12 col-sm-8 p-0 m-0 text-center text-md-left d-flex justify-content-center align-items-center">
                        <div className="w-100">
                          <textarea className="form-control mr-3" maxLength={600} autoComplete="do-not-autofill"
                            name="SummaryOfObjection"
                            onChange={(e) => setObjectionSummary(e.target.value)}
                            value={objectionSummary}
                          />
                          <div className="row pt-1 pr-3 justify-content-end">
                            <div className={objectionSummary.length > 0 ? 'visible' : 'invisible'}>
                              <div style={{ fontSize: '0.8em' }}>{objectionSummary.length}/600</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>)}

                {/* ── Immunization Selection (all types) ── */}
                {exemptionType && (
                  <>
                    <hr />
                    <div className="form-group row col-12 mx-auto mx-sm-0" data-toggle="tooltip" data-placement="top" title="Select all Immunizations that this exemption applies to">
                      <label className="col-12 col-sm-4 col-form-label text-left px-2">
                        Select all Immunizations that this exemption applies to
                        <section className="text-danger d-inline-block px-1">*</section>
                      </label>
                      <div className="col-12 col-sm-8 p-0 m-0 text-center text-md-left d-flex justify-content-center align-items-center">
                        <Select components={noAutofillComponents} isMulti={true} isSearchable={true}
                          menuPlacement="auto"
                          placeholder={<div className="accessibilityText">Please Select...</div>}
                          noOptionsMessage={() => "No option"}
                          className="state_select w-100"
                          value={exemptImmunizations}
                          options={immunizationOptions}
                          onChange={(e: any) => {
                            const indexOfLast = e?.length - 1;
                            if (e && e[indexOfLast]?.value === 0) {
                              setExemptImmunizations(immunizations);
                            } else {
                              setExemptImmunizations(e || []);
                            }
                          }}
                          aria-label={getReactSelectAriaLabelMultiselect("Please select which Immunizations this exemption applies to:", exemptImmunizations as [{label: string, value: number}])}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>


            {/* ═══════════════════════════════════════════════════════════════
                SECTION 4b: PHYSICIAN INFORMATION (Medical only)
                ═══════════════════════════════════════════════════════════════ */}
            {exemptionType === EXEMPTION_TYPE_MEDICAL && (
              <div className="card mt-2">
                <div className="card-header verlag-bold">
                  <h4 className="m-0">Physician Information</h4>
                </div>
                <div className="card-body">
                  {buildRow('First Name',
                    <input id="PhysicianFirstName" className="form-control" maxLength={50} autoComplete="do-not-autofill" type="search"
                      name="PhysicianFirstName" aria-label="First Name Of Physician"
                      onChange={(e) => updatePhysician('PhysicianFirstName', e.target.value)}
                      value={physicianInfo.PhysicianFirstName}
                    />, 'First Name Of Physician', true
                  )}
                  {buildRow('Last Name',
                    <input id="PhysicianLastName" className="form-control" maxLength={50} autoComplete="do-not-autofill" type="search"
                      name="PhysicianLastName" aria-label="Last Name Of Physician"
                      onChange={(e) => updatePhysician('PhysicianLastName', e.target.value)}
                      value={physicianInfo.PhysicianLastName}
                    />, 'Last Name Of Physician', true
                  )}

                  {/* Physician Google Places Autocomplete */}
                  <div className="form-group row" data-toggle="tooltip" data-placement="top" title="Address">
                    <label id="PhysicianAddressLabel" htmlFor="Physicianautocomplete" className="col-12 col-sm-4 col-form-label px-3">Search Address</label>
                    <div className="col-12 col-sm-8 p-0 m-0 text-center text-md-left">
                      <AutoComplete key="Physicianautocomplete" id="Physicianautocomplete"
                        onChange={(addr) => updatePhysician('SearchAddress', addr)}
                        value={physicianInfo.SearchAddress}
                        updateFormData={(addr, city, state, zip, county, country, subpremise) => {
                          setPhysicianInfo(prev => ({
                            ...prev,
                            PhysicianAddress: addr,
                            PhysicianCity: city,
                            PhysicianState: state,
                            PhysicianZip: zip,
                            ...(subpremise ? { PhysicianAdditionalAddressInfo: subpremise } : {})
                          }));
                        }}
                      />
                    </div>
                  </div>

                  {buildRow('Street Address',
                    <input id="PhysicianStreetAddress" className="form-control" maxLength={50} autoComplete="do-not-autofill" type="search"
                      name="phys_addr_nofill" aria-label="Street Address"
                      onChange={(e) => updatePhysician('PhysicianAddress', e.target.value)}
                      value={physicianInfo.PhysicianAddress}
                    />, 'Street Address', true
                  )}
                  {buildRow('City',
                    <input id="PhysicianCity" className="form-control" maxLength={50} autoComplete="do-not-autofill" type="search"
                      name="phys_city_nofill" aria-label="City"
                      onChange={(e) => updatePhysician('PhysicianCity', e.target.value)}
                      value={physicianInfo.PhysicianCity}
                    />, 'City'
                  )}
                  {buildRow('State',
                    <Select components={noAutofillComponents} key="PhysicianState" id="PhysicianState" isSearchable={true}
                      placeholder={<div className="accessibilityText">Please Select...</div>}
                      noOptionsMessage={() => "No option"}
                      aria-label="State"
                      value={getLabel(physicianInfo.PhysicianState, states)}
                      onChange={(e: any) => updatePhysician('PhysicianState', e.value)}
                      className="state_select"
                      options={states}
                    />, 'State'
                  )}
                  {buildRow('Zipcode',
                    <input id="PhysicianZipcode" className="form-control" maxLength={50} autoComplete="do-not-autofill" type="search"
                      name="phys_zip_nofill" aria-label="Zipcode"
                      onChange={(e) => updatePhysician('PhysicianZip', e.target.value)}
                      value={physicianInfo.PhysicianZip}
                    />, 'Zipcode'
                  )}
                  {buildRow('Apartment/Suite/Building #',
                    <input id="PhysicianAdditionalAddress" className="form-control" maxLength={50} autoComplete="do-not-autofill" type="search"
                      name="phys_apt_nofill" aria-label="Apartment/Suite/Building #"
                      onChange={(e) => updatePhysician('PhysicianAdditionalAddressInfo', e.target.value)}
                      value={physicianInfo.PhysicianAdditionalAddressInfo}
                    />, 'Apartment/Suite/Building #'
                  )}
                  {buildRow('Office Phone',
                    <PhoneInput id="PhysicianPhone" placeholder="Enter phone number"
                      onChange={(e) => updatePhysician('PhysicianPhone', e)}
                      defaultCountry="US" aria-label="Office Phone"
                      value={physicianInfo.PhysicianPhone}
                    />, 'Office Phone'
                  )}

                </div>
              </div>
            )}


            {/* ═══════════════════════════════════════════════════════════════
                STATUS
                ═══════════════════════════════════════════════════════════════ */}
            <div className="card mb-2 mt-2">
              <div className="card-header verlag-bold">
                <h4 className="m-0">Exemption Status</h4>
              </div>
              <div className="card-body">
                {buildRow('Status',
                  <Select components={noAutofillComponents} key="ExemptionStatus" id="ExemptionStatus" isSearchable={false}
                    placeholder={<div className="accessibilityText">Please Select...</div>}
                    aria-label="Exemption Status"
                    value={
                      exemptionStatus === 1 ? { label: 'Approved', value: 1 }
                      : exemptionStatus === 0 ? { label: 'Denied', value: 0 }
                      : exemptionStatus === null ? { label: 'Pending', value: null }
                      : null
                    }
                    onChange={(e: any) => {
                      setExemptionStatus(e.value);
                      if (e.value !== 0) {
                        setDenialReason(null);
                        setOtherDenialReason("");
                      }
                    }}
                    className="state_select"
                    options={[
                      { label: 'Approved', value: 1 },
                      { label: 'Pending', value: null },
                      { label: 'Denied', value: 0 },
                    ]}
                  />, 'Status', true
                )}
                {exemptionStatus === 0 && buildRow('Denial Reason',
                  <Select components={noAutofillComponents} key="DenialReason" id="DenialReason" isSearchable={true}
                    placeholder={<div className="accessibilityText">Please Select...</div>}
                    aria-label="Denial Reason"
                    value={denialReason}
                    onChange={(e: any) => {
                      setDenialReason(e);
                      if (e?.value !== "Other") setOtherDenialReason("");
                    }}
                    className="state_select"
                    options={[
                      { label: "Fraudulent Request", value: "Fraudulent Request" },
                      { label: "Duplicate Exemption", value: "Duplicate Exemption" },
                      { label: "Other", value: "Other" },
                    ]}
                  />, 'Denial Reason', true
                )}
                {exemptionStatus === 0 && denialReason?.value === "Other" && buildRow('Other Denial Reason',
                  <input
                    id="OtherDenialReason"
                    className="form-control"
                    autoComplete="do-not-autofill"
                    type="search"
                    name="denial_other_nofill"
                    aria-label="Other Denial Reason"
                    value={otherDenialReason}
                    onChange={(e) => setOtherDenialReason(e.target.value)}
                  />, 'Other Denial Reason'
                )}
              </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════════
                SUBMIT
                ═══════════════════════════════════════════════════════════════ */}
            <div className="card mb-2 mt-2">
              <div className="card-footer d-flex justify-content-between">
                <button type="button" className="btn btn-outline-primary" onClick={() => submitForm()}>Submit</button>
                <button type="button" className="btn btn-outline-secondary" onClick={() => {
                  sweetalert.fire({
                    icon: "warning",
                    title: "Clear Form",
                    text: "Are you sure you want to clear all fields?",
                    showCancelButton: true,
                    confirmButtonText: "Clear",
                    cancelButtonText: "Cancel",
                  }).then((result) => {
                    if (result.isConfirmed) resetForm();
                  });
                }}>Clear Form</button>
              </div>
            </div>

          </main>
        </div>
      </div>
      </form>
    </div>
  );
};

export default VaccineExemptionsForm;
